/**
 * Draft Manager Utility
 * 
 * Handles client-side draft saving with the following requirements:
 * - Saves only one draft that gets overwritten every save interval
 * - Uses 10-second save interval (configurable via REACT_APP_DRAFT_SAVE_INTERVAL_SEC)
 * - Implements 10-minute maximum age check
 * - Deletes draft on submission
 * - Makes draft saving invisible to users (no visual indicators)
 */

import { NoteFormData } from '../components/common/NotesForm';

// Default save interval in seconds (10 seconds)
const DEFAULT_SAVE_INTERVAL = 10;
// Maximum draft age in minutes (10 minutes)
const MAX_DRAFT_AGE_MINUTES = 10;

// Get save interval from environment or use default
const getSaveInterval = (): number => {
  const envInterval = process.env.REACT_APP_DRAFT_SAVE_INTERVAL_SEC;
  return envInterval ? parseInt(envInterval, 10) : DEFAULT_SAVE_INTERVAL;
};

// Draft storage key
const DRAFT_STORAGE_KEY = 'notestream_note_draft';

/**
 * Saves form data as a draft to localStorage
 * @param formData The note form data to save
 */
export const saveDraft = (formData: NoteFormData): void => {
  if (!formData.note || formData.note.trim() === '') {
    return; // Don't save empty drafts
  }
  
  const draft = {
    data: formData,
    timestamp: new Date().toISOString()
  };
  
  localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft));
};

/**
 * Loads a draft from localStorage if available and not expired
 * @returns The draft form data or null if no valid draft exists
 */
export const loadDraft = (): NoteFormData | null => {
  const draftJson = localStorage.getItem(DRAFT_STORAGE_KEY);
  
  if (!draftJson) {
    return null;
  }
  
  try {
    const draft = JSON.parse(draftJson);
    const draftDate = new Date(draft.timestamp);
    const now = new Date();
    const ageInMinutes = (now.getTime() - draftDate.getTime()) / (1000 * 60);
    
    // Check if draft is older than maximum age
    if (ageInMinutes > MAX_DRAFT_AGE_MINUTES) {
      deleteDraft(); // Delete expired draft
      return null;
    }
    
    return draft.data;
  } catch (error) {
    console.error('Error parsing draft:', error);
    return null;
  }
};

/**
 * Deletes the saved draft from localStorage
 */
export const deleteDraft = (): void => {
  localStorage.removeItem(DRAFT_STORAGE_KEY);
};

/**
 * Sets up automatic draft saving at specified interval
 * @param getFormData Function that returns current form data
 * @returns Cleanup function to clear the interval
 */
export const setupAutosave = (getFormData: () => NoteFormData): () => void => {
  const intervalId = setInterval(() => {
    const formData = getFormData();
    saveDraft(formData);
  }, getSaveInterval() * 1000);
  
  // Return cleanup function
  return () => clearInterval(intervalId);
}; 